import React, { ReactNode } from "react"
import { Box, Text } from "@chakra-ui/react"
import Menu from "components/dialogs/Menu"
import Icon from "components/elements/Icon"
import {
  PillShell, PillVariant, PILL_SIZES, PILL_STYLES,
} from "components/TemplateEditor/VariablePill"
import GripIcon from "./GripIcon"
import RemoveTagButton from "./RemoveTagButton"

export type MergeTagPillDisplayProps = {
  tagLabel: string
  variant: PillVariant
  maxW?: string
  size?: "sm" | "md" | "lg"
  readOnly?: boolean
  menuItems?: ReactNode[]
  onRemove?: () => void
}

const pillLabel = (tagLabel: string, maxW?: string) => (
  <Text
    overflow="hidden"
    textOverflow="ellipsis"
    whiteSpace="nowrap"
    maxW={maxW}
  >
    {tagLabel}
  </Text>
)

const PillTrailingIcon = ({ variant }: { variant: PillVariant }) => (
  <Icon icon={PILL_STYLES[variant].icon} size={PILL_SIZES.md.iconSize} />
)

const menuPillTriggerContent = (variant: PillVariant, tagLabel: string, maxW?: string) => (
  <>
    <GripIcon />
    {pillLabel(tagLabel, maxW)}
    <PillTrailingIcon variant={variant} />
  </>
)

const menuPillButton = (variant: PillVariant, tagLabel: string, maxW?: string, size: "sm" | "md" | "lg" = "md") => (
  <PillShell variant={variant} size={size} cursor="grab" showTrailingIcon={false}>
    {menuPillTriggerContent(variant, tagLabel, maxW)}
  </PillShell>
)

/** Visual shell shared by Lexical editor pills and read-only subject previews. */
const MergeTagPillDisplay = ({
  tagLabel,
  variant,
  maxW,
  size = "md",
  readOnly = false,
  menuItems,
  onRemove,
}: MergeTagPillDisplayProps) => {
  if (!readOnly && menuItems && onRemove) {
    return (
      <PillShell variant={variant} size={size} cursor="grab" showTrailingIcon={false}>
        <Menu
          placement="bottom"
          noPadding
          title="Change merge field"
          button={(
            <Box
              as="span"
              display="inline-flex"
              alignItems="center"
              flex={1}
              minW={0}
              gap={PILL_SIZES[size].gap}
              cursor="pointer"
              pointerEvents="auto"
            >
              {menuPillTriggerContent(variant, tagLabel, maxW)}
            </Box>
          )}
          menuItems={menuItems}
          maxH="50vh"
        />
        <RemoveTagButton tagLabel={tagLabel} onRemove={onRemove} />
      </PillShell>
    )
  }

  if (!readOnly && menuItems) {
    return (
      <Menu
        placement="bottom"
        noPadding
        title="Change merge field"
        button={menuPillButton(variant, tagLabel, maxW, size)}
        menuItems={menuItems}
        maxH="50vh"
      />
    )
  }

  return (
    <PillShell
      variant={variant}
      size={size}
      cursor={readOnly ? "default" : "grab"}
      showTrailingIcon={!readOnly && !menuItems}
    >
      {!readOnly && <GripIcon />}
      {pillLabel(tagLabel, maxW)}
    </PillShell>
  )
}

export default MergeTagPillDisplay
